import { styled, Theme, CSSObject } from '@mui/material/styles';

import Divider from '@mui/material/Divider';
import Icon from '../IconSvg/Icon';
import { GlobalStyles, List, Stack } from '@mui/material';
import { useState } from 'react';
import { StackProps } from '@mui/material';
import LelyNavigationListItem, {
  MenuNavigationItem,
} from './LelyNavigationListItem';
import { useKeyPress } from '@/components/App/hooks';
import {
  NAVIGATION_MENU_CLOSED_WIDTH as CLOSED_WIDTH,
  NAVIGATION_MENU_EXPANDED_WIDTH as EXPANDED_WIDTH,
  NAVIGATION_MENU_WIDTH_VAR,
} from './navigationMenuConstants';

const EXPAND_LABEL_TOOLTIP = 'Expand menu [Ctrl+M]';
const COLLAPSE_LABEL = 'Collapse menu';

const expandedStyles = (theme: Theme): CSSObject => ({
  width: EXPANDED_WIDTH,
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.lelyTransition,
  }),
});

const collapsedStyles = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.lelyTransition,
  }),
  width: CLOSED_WIDTH,
});

type NavigationMenuContainerProps = {
  isExpanded: boolean;
} & StackProps;

const NavigationMenuContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<NavigationMenuContainerProps>(({ theme, isExpanded }) => ({
  flexShrink: 0,
  position: 'relative',
  // Above drawer backdrops, below dialogs
  zIndex: theme.zIndex.drawer + 1,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.brandPrimary,
  height: '100vh',
  overflowX: 'hidden',
  padding: theme.lelySpacing.XS,
  gap: theme.lelySpacing.XS,
  alignItems: 'normal',
  borderTopRightRadius: theme.lelyBorderRadius.LG,

  ...(isExpanded ? expandedStyles(theme) : collapsedStyles(theme)),
}));

type Props = {
  avatarItem: MenuNavigationItem;

  topSectionItems?: MenuNavigationItem[];
  middleSectionItems?: MenuNavigationItem[];
  bottomSectionItems?: MenuNavigationItem[];
};

const LelyNavigationMenu = ({
  avatarItem,
  topSectionItems,
  middleSectionItems,
  bottomSectionItems = [],
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleNavigationMenuToggle = () => setIsExpanded((prev) => !prev);

  useKeyPress([{ key: 'm', ctrlKey: true }], handleNavigationMenuToggle);

  const renderItems = (items: MenuNavigationItem[]) =>
    items.map((item) => (
      <LelyNavigationListItem
        key={item.label}
        {...item}
        isExpanded={isExpanded}
      />
    ));

  return (
    <NavigationMenuContainer isExpanded={isExpanded} component="nav">
      <GlobalStyles
        styles={{
          ':root': {
            [NAVIGATION_MENU_WIDTH_VAR]: `${
              isExpanded ? EXPANDED_WIDTH : CLOSED_WIDTH
            }px`,
          },
        }}
      />
      <List>{renderItems([avatarItem])}</List>
      <Divider />

      {topSectionItems && <List>{renderItems(topSectionItems)}</List>}

      {middleSectionItems && (
        <>
          <Divider />
          <List>{renderItems(middleSectionItems)}</List>
        </>
      )}

      <List sx={{ marginTop: 'auto' }}>
        {renderItems(bottomSectionItems)}

        <Divider />

        <LelyNavigationListItem
          label={COLLAPSE_LABEL}
          testId="navigation-menu-toggle"
          tooltipLabel={EXPAND_LABEL_TOOLTIP}
          icon={
            <Icon
              name={
                isExpanded
                  ? 'lely-icon-collapse-panel'
                  : 'lely-icon-expand-panel'
              }
            />
          }
          isExpanded={isExpanded}
          onClick={handleNavigationMenuToggle}
        />
      </List>
    </NavigationMenuContainer>
  );
};

export default LelyNavigationMenu;
